// Action-id-aware product matcher for soil-test interpretations.
//
// Picks resolve to catalog entries in `data/guide-product-recommendations.ts`
// via the shared resolver (`lib/product-picks/resolve.ts`). That catalog is the
// single source of truth for product names, ASINs, affiliate URLs, prices, and
// images — this module only owns the (action.id → [catalog keys + reason])
// mapping. Catalog updates flow through automatically; no duplicated fields here.
//
// The generic resolver/dedupe/price-sum helpers now live in `lib/product-picks/`
// so every calculator shares them; this file is just the soil-test prescription map.
//
// Why id-based and not category-based: `category` is a taxonomy (ph,
// organic_matter, ...) that maps many-to-one against actions that prescribe
// opposite things — e.g. `ph` covers both `ph-low-apply-lime` (lime, raises
// pH) and `ph-high-apply-sulfur` (sulfur, lowers pH). A single category map
// would force opposite products into the same slot. Action id is the
// prescription; products are responses to prescriptions.
//
// Substitutes: two actionable ids (`ph-high-apply-sulfur`, `k-low-apply`)
// don't have a pure-product match in the catalog today (no elemental sulfur,
// no sulfate of potash). They resolve to the closest defensible match
// (Jonathan Green Mag-I-Cal Plus for alkaline soil; Lilly Miller 16-16-16 for
// balanced K). The action card surfaces a one-sentence caveat from
// `interpret.ts` so users know what the agronomic preference is.

import type { CatalogPick, ProductRecommendationLink } from '@/lib/product-picks/types'
import { dedupeLinks, resolvePicks, sumPrices } from '@/lib/product-picks/resolve'
import type { RecommendedAction } from './types'

// Re-export so the soil-test results page keeps importing the shopping-list
// total from here (back-compat after the resolver moved to lib/product-picks/).
export { sumPrices }

// Action id → ordered picks. Order matters: index 0 is the primary
// recommendation, 1-2 are alternatives. Catalog gaps resolve to the closest
// defensible substitute (see file header).
const PICKS: Record<string, CatalogPick[]> = {
  'ph-low-apply-lime': [
    {
      key: 'soil-lime-pennington',
      reason: 'Pelletized lime — easier to spread than powdered, faster acting than ag lime.',
    },
    {
      key: 'soil-compost-coast-of-maine',
      reason: 'Compost while you correct pH — builds the soil structure lime alone can\'t.',
    },
  ],
  'ph-high-apply-sulfur': [
    {
      key: 'soil-magical-alkaline',
      reason: 'Soil amendment sold specifically for alkaline soil — the same job elemental sulfur does.',
    },
    {
      key: 'iron-southern-ag',
      reason: 'Chelated iron treats the yellowing (iron chlorosis) that high pH typically causes.',
    },
    {
      key: 'iron-petratools',
      reason: 'Liquid iron alternative — faster green-up than granular while pH adjusts.',
    },
  ],
  'k-low-apply': [
    {
      key: 'fertilizer-lilly-miller-161616',
      reason: 'Balanced 16-16-16 — highest K in our catalog (16% K2O) and chloride-free enough for most lawns.',
    },
    {
      key: 'fertilizer-neighbors-envy',
      reason: '7-in-1 liquid with potassium plus micronutrients in a single pass.',
    },
    {
      key: 'fertilizer-pgf-complete',
      reason: '16-4-8 slow-release — steady K plus nitrogen without a quick top-growth flush.',
    },
  ],
  'p-low-overseed-starter': [
    {
      key: 'starter-scotts',
      reason: 'Starter fertilizer with the planting-time phosphorus boost new seedlings need.',
    },
  ],
  'om-very-low-amend': [
    {
      key: 'soil-compost-coast-of-maine',
      reason: 'Compost for topdressing low-organic-matter lawns after aeration.',
    },
    {
      key: 'soil-humic-acid',
      reason: 'Humic acid concentrate — boosts soil biology and CEC alongside compost.',
    },
    {
      key: 'soil-sea-kelp',
      reason: 'Kelp extract adds trace minerals and microbial food, compounds with compost over the season.',
    },
  ],
  'om-low-topdress': [
    {
      key: 'soil-compost-coast-of-maine',
      reason: 'Compost for annual quarter-inch topdressing to lift organic matter over time.',
    },
    {
      key: 'soil-humic-acid',
      reason: 'Humic acid concentrate — pairs with light topdressing for sustained soil biology.',
    },
  ],
}

/**
 * Return curated affiliate product picks for an action id.
 * Returns [] for informational actions and for actionable ids without a map
 * entry. Also accepts a full `RecommendedAction` for callers that already
 * have one in hand.
 */
export function getSoilTestProducts(
  actionOrId: string | Pick<RecommendedAction, 'id'>,
): ProductRecommendationLink[] {
  const id = typeof actionOrId === 'string' ? actionOrId : actionOrId.id
  const picks = PICKS[id]
  if (!picks) return []
  return resolvePicks(picks)
}

/**
 * Dedupe products across multiple actions. Key by ASIN when present, else by
 * name. Order preserved by first appearance, so the primary pick from the
 * first action with a deficiency shows up first in the shopping list.
 */
export function dedupeProducts(actions: RecommendedAction[]): ProductRecommendationLink[] {
  return dedupeLinks(actions.flatMap((a) => a.products))
}
